/**
 * Procurement & Due Diligence — Iteration 120
 *
 * Public enterprise-grade Trust Centre tab. Renders the
 * `/api/trust/due-diligence` payload exactly as the backend
 * produces it — no client-side opinions added. Includes:
 *
 *  - Security overview
 *  - Compliance roadmap (DB-driven)
 *  - GDPR (controller, lawful basis, DSAR endpoints)
 *  - Data Processing Agreement (DPA)
 *  - Security architecture
 *  - Encryption standards (incl. field-level)
 *  - Backup & DR (live RPO/RTO + drill history)
 *  - Business continuity
 *  - Penetration testing history (live from audit_log)
 *  - Vulnerability disclosure programme
 *  - Audit reports available on request (with NDA gating)
 *  - ISO 27001 / SOC 2 readiness
 *  - Future certifications (ISO 27701, CE+, CSA STAR, NIS2, HIPAA)
 *  - Security contact
 *  - Request the audit pack (POST /api/trust/audit-pack-request)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ShieldCheck, Lock, Database, FileText, AlertTriangle, CheckCircle2,
  ScrollText, Mail, Clock, Map, Server, Zap, BookOpen,
} from "lucide-react";

const STATUS_TONE = {
  certified: { chip: "bg-emerald-100", text: "text-emerald-800" },
  available: { chip: "bg-emerald-100", text: "text-emerald-800" },
  pass: { chip: "bg-emerald-100", text: "text-emerald-800" },
  in_progress: { chip: "bg-amber-100", text: "text-amber-900" },
  planned: { chip: "bg-slate-100", text: "text-slate-700" },
  deferred: { chip: "bg-slate-100", text: "text-slate-500" },
  pending: { chip: "bg-slate-100", text: "text-slate-700" },
  warn: { chip: "bg-amber-100", text: "text-amber-900" },
  fail: { chip: "bg-rose-100", text: "text-rose-900" },
};

function StatusChip({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.in_progress;
  return (
    <Badge className={`${t.chip} ${t.text} border-0 rounded-sm text-[10px] uppercase tracking-wider px-1.5 py-0.5`}>
      {(status || "in_progress").replace(/_/g, " ")}
    </Badge>
  );
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <Icon className="w-5 h-5 text-teal-700" /> {title}
      </h2>
      <Card className="p-5">{children}</Card>
    </section>
  );
}

function KV({ k, v }) {
  return (
    <div className="text-sm">
      <span className="text-slate-500">{k}</span>{" "}
      <span className="text-slate-900 font-mono-data">{v ?? "—"}</span>
    </div>
  );
}

export default function DueDiligence() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reqForm, setReqForm] = useState({
    name: "", company: "", email: "", role: "", reason: "",
  });
  const [reqBusy, setReqBusy] = useState(false);

  useEffect(() => {
    api.get("/trust/due-diligence")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  }, []);

  const submitRequest = async (e) => {
    e.preventDefault();
    setReqBusy(true);
    try {
      await api.post("/trust/audit-pack-request", reqForm);
      toast.success("Request received — our compliance team will be in touch.");
      setReqForm({ name: "", company: "", email: "", role: "", reason: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setReqBusy(false);
    }
  };

  if (error) {
    return (
      <main className="min-h-screen bg-slate-50 p-8">
        <Card className="max-w-2xl mx-auto p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <div className="font-semibold text-slate-900 mb-1">Due Diligence page unavailable</div>
              <div className="text-sm text-slate-700">{error}</div>
            </div>
          </div>
        </Card>
      </main>
    );
  }
  if (!data) return <main className="p-8 text-sm text-slate-600">Loading procurement &amp; due diligence pack…</main>;

  return (
    <main className="min-h-screen bg-slate-50" data-testid="due-diligence-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3 mb-3">
            <ShieldCheck className="w-7 h-7 text-teal-700" />
            <h1 className="text-3xl font-semibold text-slate-900">Procurement &amp; Due Diligence</h1>
          </div>
          <p className="text-slate-700 max-w-3xl">
            Enterprise-grade Trust Centre detail for solicitors, mediators, courts, organisations and procurement teams. Every figure is derived from the platform&apos;s live state — never marketing copy. Audit reports and a full evidence pack are available on request under NDA.
          </p>
          <p className="text-[11px] text-slate-500 mt-3 font-mono-data">
            Generated {data.generated_at?.slice(0, 19)} · Operator {data.platform?.operator} · Region {data.platform?.region}
          </p>
          <div className="mt-4">
            <Link to="/trust" className="text-sm text-teal-700 hover:underline">← Back to Trust Centre</Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* 1. Security overview */}
        <Section icon={ShieldCheck} title="Security overview" testId="dd-section-overview">
          <p className="text-sm text-slate-700">{data.security_overview?.summary}</p>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <KV k="Controls implemented" v={data.security_overview?.controls_implemented} />
            <KV k="Active AV engine" v={data.security_overview?.active_av_engine} />
            <KV k="Security status %" v={`${data.security_overview?.summary_scores?.security_status_pct ?? "—"}%`} />
            <KV k="Data protection %" v={`${data.security_overview?.summary_scores?.data_protection_pct ?? "—"}%`} />
          </dl>
        </Section>

        {/* 2. Compliance roadmap */}
        <Section icon={Map} title="Compliance roadmap" testId="dd-section-roadmap">
          <ul className="divide-y divide-slate-100">
            {data.compliance_roadmap.map((row, i) => (
              <li key={i} className="py-3 flex items-baseline gap-3 flex-wrap">
                <span className="font-mono-data text-xs text-slate-500 w-20">{row.target_quarter}</span>
                <span className="text-sm font-medium text-slate-900">{row.standard}</span>
                <span className="text-xs text-slate-600 flex-1 min-w-0">{row.phase}</span>
                <StatusChip status={row.status} />
              </li>
            ))}
          </ul>
        </Section>

        {/* 3. GDPR */}
        <Section icon={BookOpen} title="GDPR" testId="dd-section-gdpr">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <KV k="Controller" v={data.gdpr?.controller} />
            <KV k="DPO" v={data.gdpr?.data_protection_officer} />
            <KV k="DSAR endpoint" v={data.gdpr?.dsar_endpoint} />
            <KV k="Deletion endpoint" v={data.gdpr?.deletion_endpoint} />
          </div>
          <div className="mt-3 text-sm">
            <div className="text-slate-500 mb-1">Lawful basis</div>
            <ul className="list-disc list-inside text-slate-700 space-y-0.5">
              {(data.gdpr?.lawful_basis || []).map((b) => <li key={b}>{b}</li>)}
            </ul>
          </div>
          <div className="mt-3 text-sm">
            <div className="text-slate-500 mb-1">Data-subject rights honoured</div>
            <div className="flex flex-wrap gap-1.5">
              {(data.gdpr?.data_subject_rights || []).map((r) => (
                <Badge key={r} className="bg-slate-100 text-slate-700 border-0 rounded-sm text-[11px]">{r}</Badge>
              ))}
            </div>
          </div>
        </Section>

        {/* 4. DPA */}
        <Section icon={ScrollText} title="Data Processing Agreement (DPA)" testId="dd-section-dpa">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <KV k="DPA available on request" v={data.dpa?.available_on_request ? "Yes" : "No"} />
            <KV k="SCC framework" v={data.dpa?.standard_contractual_clauses} />
            <KV k="Sub-processors register" v={data.dpa?.sub_processors_register_url} />
          </div>
        </Section>

        {/* 5. Security architecture */}
        <Section icon={Server} title="Security architecture overview" testId="dd-section-architecture">
          <dl className="grid grid-cols-1 gap-3 text-sm">
            {Object.entries(data.security_architecture || {}).map(([k, v]) => (
              <div key={k}>
                <dt className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</dt>
                <dd className="text-slate-800">{v}</dd>
              </div>
            ))}
          </dl>
        </Section>

        {/* 6. Encryption standards */}
        <Section icon={Lock} title="Encryption standards" testId="dd-section-encryption">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-medium text-slate-900 mb-1">At rest</div>
              <KV k="Provider" v={data.encryption_standards?.at_rest?.provider} />
              <KV k="Algorithm" v={data.encryption_standards?.at_rest?.algorithm} />
              <KV k="Field-level envelope" v={data.encryption_standards?.at_rest?.field_level_envelope ? "Active (AES-256-GCM)" : "Not configured"} />
            </div>
            <div>
              <div className="font-medium text-slate-900 mb-1">In transit</div>
              <KV k="Min TLS" v={data.encryption_standards?.in_transit?.min_tls} />
              <KV k="HSTS preloaded" v={data.encryption_standards?.in_transit?.hsts_preloaded ? "Yes" : "No"} />
              <KV k="Internal DB TLS" v={data.encryption_standards?.in_transit?.internal_db_tls ? "Yes" : "No"} />
            </div>
          </div>
          <div className="mt-4 text-xs text-slate-600">
            <div className="font-medium text-slate-700 mb-1">Key management</div>
            <div>Model: {data.encryption_standards?.key_management?.model}</div>
            <div>Rotation supported: {data.encryption_standards?.key_management?.rotation_supported ? "Yes" : "No"}</div>
            <div>Future: {data.encryption_standards?.key_management?.future}</div>
          </div>
        </Section>

        {/* 7. Backup & DR */}
        <Section icon={Database} title="Backup & disaster recovery" testId="dd-section-backup">
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <KV k="RPO" v={`${data.backup_and_dr?.rpo_minutes} min`} />
            <KV k="RTO" v={`${data.backup_and_dr?.rto_minutes} min`} />
            <KV k="Snapshot provider" v={data.backup_and_dr?.snapshot_provider} />
            <KV k="Geo redundancy" v={data.backup_and_dr?.geo_redundancy} />
          </dl>
          {data.backup_and_dr?.drill_history?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase text-slate-500 mb-1 tracking-wider">Drill history</div>
              <ul className="text-sm space-y-1">
                {data.backup_and_dr.drill_history.map((d, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="font-mono-data">{d.date}</span>
                    <StatusChip status={d.outcome} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        {/* 8. Business continuity */}
        <Section icon={Zap} title="Business continuity" testId="dd-section-continuity">
          <KV k="BCP" v={<Link to={data.business_continuity?.bcp_url || "#"} className="text-teal-700 hover:underline">{data.business_continuity?.bcp_url}</Link>} />
          <KV k="IR" v={<Link to={data.business_continuity?.incident_response_url || "#"} className="text-teal-700 hover:underline">{data.business_continuity?.incident_response_url}</Link>} />
          <KV k="Tested quarterly" v={data.business_continuity?.tested_quarterly ? "Yes" : "No"} />
          <KV k="On-call escalation documented" v={data.business_continuity?.on_call_escalation_documented ? "Yes" : "No"} />
        </Section>

        {/* 9. Pentest history */}
        <Section icon={ShieldCheck} title="Penetration testing history" testId="dd-section-pentest">
          {(data.pentest_history || []).length === 0 ? (
            <p className="text-sm text-slate-600">No penetration test reports on file yet. First engagement is in flight per the compliance roadmap.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.pentest_history.map((p, i) => (
                <li key={i} className="py-3 grid grid-cols-1 sm:grid-cols-4 gap-2 text-sm">
                  <span className="font-mono-data text-slate-700">{p.date}</span>
                  <span className="text-slate-900">{p.vendor}</span>
                  <span className="text-slate-600">Report {p.report_id}</span>
                  <span className="text-slate-700 sm:text-right">{p.findings_count ?? 0} finding(s)</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* 10–11. Vulnerability disclosure programme */}
        <Section icon={Mail} title="Vulnerability disclosure & responsible reporting" testId="dd-section-vdp">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <KV k="Policy" v={<Link to={data.vulnerability_disclosure?.policy_url} className="text-teal-700 hover:underline">{data.vulnerability_disclosure?.policy_url}</Link>} />
            <KV k="security.txt" v={<a href={data.vulnerability_disclosure?.security_txt} className="text-teal-700 hover:underline">{data.vulnerability_disclosure?.security_txt}</a>} />
            <KV k="Contact" v={<a href={`mailto:${data.vulnerability_disclosure?.contact_email}`} className="text-teal-700 hover:underline">{data.vulnerability_disclosure?.contact_email}</a>} />
            <KV k="Safe harbour" v={data.vulnerability_disclosure?.safe_harbour ? "Yes" : "No"} />
          </div>
          <div className="mt-3 text-sm">
            <div className="text-slate-500 mb-1">In-scope</div>
            <ul className="list-disc list-inside text-slate-700">
              {(data.vulnerability_disclosure?.scope || []).map((s) => <li key={s} className="font-mono-data">{s}</li>)}
            </ul>
          </div>
        </Section>

        {/* 12. Audit reports */}
        <Section icon={FileText} title="Audit reports available on request" testId="dd-section-audit-reports">
          <p className="text-sm text-slate-700 mb-3">
            All reports are released under NDA. Send a request to{" "}
            <a className="text-teal-700 hover:underline" href={`mailto:${data.audit_reports?.request_email}`}>
              {data.audit_reports?.request_email}
            </a>{" "}
            or use the form at the bottom of this page.
          </p>
          <ul className="divide-y divide-slate-100">
            {(data.audit_reports?.reports_available || []).map((r) => (
              <li key={r.name} className="py-2 flex items-baseline justify-between gap-3">
                <span className="text-sm text-slate-900">{r.name}</span>
                <StatusChip status={r.status} />
              </li>
            ))}
          </ul>
        </Section>

        {/* 13/14. ISO + SOC */}
        <Section icon={CheckCircle2} title="ISO 27001 & SOC 2 readiness" testId="dd-section-iso-soc">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="p-4 bg-slate-50 border-slate-200">
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-900">ISO 27001</span>
                <StatusChip status={data.iso27001_readiness?.status} />
              </div>
              <p className="text-xs text-slate-700 mt-2">{data.iso27001_readiness?.details}</p>
              <p className="text-[11px] text-slate-500 mt-2 font-mono-data">Readiness: {data.iso27001_readiness?.readiness_pct ?? "—"}%</p>
            </Card>
            <Card className="p-4 bg-slate-50 border-slate-200">
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-900">SOC 2 Type II</span>
                <StatusChip status={data.soc2_readiness?.status} />
              </div>
              <p className="text-xs text-slate-700 mt-2">{data.soc2_readiness?.details}</p>
              <p className="text-[11px] text-slate-500 mt-2 font-mono-data">Readiness: {data.soc2_readiness?.readiness_pct ?? "—"}%</p>
            </Card>
          </div>
        </Section>

        {/* 15. Future certifications */}
        <Section icon={Clock} title="Future certifications" testId="dd-section-future-certs">
          <ul className="divide-y divide-slate-100">
            {(data.future_certifications || []).map((c) => (
              <li key={c.standard} className="py-3">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <span className="text-sm font-medium text-slate-900">{c.standard}</span>
                  <StatusChip status={c.status} />
                </div>
                <p className="text-xs text-slate-600 mt-1">{c.details}</p>
              </li>
            ))}
          </ul>
        </Section>

        {/* 16. Security contact + audit pack request */}
        <Section icon={Mail} title="Security contact" testId="dd-section-contact">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <KV k="General" v={<a className="text-teal-700 hover:underline" href={`mailto:${data.security_contact?.general}`}>{data.security_contact?.general}</a>} />
            <KV k="Compliance" v={<a className="text-teal-700 hover:underline" href={`mailto:${data.security_contact?.compliance}`}>{data.security_contact?.compliance}</a>} />
            <KV k="DPO" v={<a className="text-teal-700 hover:underline" href={`mailto:${data.security_contact?.dpo}`}>{data.security_contact?.dpo}</a>} />
            <KV k="Abuse" v={<a className="text-teal-700 hover:underline" href={`mailto:${data.security_contact?.abuse}`}>{data.security_contact?.abuse}</a>} />
          </dl>
        </Section>

        <Section icon={FileText} title="Request the audit evidence pack" testId="dd-section-request">
          <form onSubmit={submitRequest} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="audit-pack-form">
            <div>
              <Label className="text-xs">Your name</Label>
              <Input value={reqForm.name} onChange={(e) => setReqForm({ ...reqForm, name: e.target.value })} required data-testid="audit-pack-name" />
            </div>
            <div>
              <Label className="text-xs">Company / organisation</Label>
              <Input value={reqForm.company} onChange={(e) => setReqForm({ ...reqForm, company: e.target.value })} required data-testid="audit-pack-company" />
            </div>
            <div>
              <Label className="text-xs">Work email</Label>
              <Input type="email" value={reqForm.email} onChange={(e) => setReqForm({ ...reqForm, email: e.target.value })} required data-testid="audit-pack-email" />
            </div>
            <div>
              <Label className="text-xs">Role (optional)</Label>
              <Input value={reqForm.role} onChange={(e) => setReqForm({ ...reqForm, role: e.target.value })} data-testid="audit-pack-role" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Reason for request</Label>
              <Textarea value={reqForm.reason} onChange={(e) => setReqForm({ ...reqForm, reason: e.target.value })} required rows={3} data-testid="audit-pack-reason" />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={reqBusy} className="bg-teal-700 hover:bg-teal-800 text-white rounded-sm" data-testid="audit-pack-submit">
                {reqBusy ? "Sending…" : "Request audit pack"}
              </Button>
            </div>
          </form>
          <p className="text-[11px] text-slate-500 mt-3">
            Requests are written to an audited collection and reviewed by our compliance team. We respond under NDA within 5 business days.
          </p>
        </Section>

      </div>
    </main>
  );
}
